import { isAxiosError } from 'axios';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import captureException from 'utils/captureException';

// Churn that says nothing about this feature: the user is signed out, or the
// extension reloaded mid-flight. Both surface on the requests the user is
// actually waiting on, so they are not worth a Sentry event here.
function isExpectedTransportError(error: unknown): boolean {
  if (isAxiosError(error) && error.response?.status === 401) {
    return true;
  }
  // Not `instanceof Error`: crossing an extension context boundary structured-clones the
  // error, so it arrives as a plain object and the prototype check would miss the very
  // case this exists for. searchLocations and createLoad match on the message alone too.
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { message?: unknown }).message ===
      'Extension context invalidated.'
  );
}

// A 5xx means the write failed server-side, where it is already logged with the suggestion and
// service ids. Reporting it again here would turn a single database incident into one Sentry
// event per sidebar tab open. 4xx is different — that means we sent something wrong, which is a
// bug on this side and worth surfacing.
function isLoggedServerSide(error: unknown): boolean {
  return isAxiosError(error) && (error.response?.status ?? 0) >= 500;
}

// A wayward render loop must not be able to turn one sidebar session into
// thousands of writes. Every POST also appends to suggestion_opened_at_history,
// so spam inflates that row's JSONB as well as the request count.
//
// This mirrors the guard useQuoteConfirmationPing puts on its default ping, but
// keyed in module scope rather than a ref: LoadBuildingTab sits in a
// TabsContent without forceMount, so it unmounts whenever the user leaves the
// tab and a ref would be reinitialised with it. Module scope lives as long as
// the extension context, which is what makes leave-and-come-back cheap.
//
// A cooldown rather than send-once-ever, because a rep returning to a
// suggestion later is real signal about rework and history exists to record it.
const RESEND_COOLDOWN_MS = 60_000;
const lastSentAt = new Map<number, number>();

// Entries past the cooldown can no longer block a send, so they are dead
// weight. Sweeping keeps the map proportional to the last minute of activity
// instead of the whole session. Only runs on an accepted send, never on a
// rejected one, so the loop this guards against does not pay for it.
function pruneExpired(now: number): void {
  lastSentAt.forEach((sentAt, suggestionId) => {
    if (now - sentAt >= RESEND_COOLDOWN_MS) {
      lastSentAt.delete(suggestionId);
    }
  });
}

// Fire-and-forget open tracking for time-to-build. Unlike the rest of lib/api
// this deliberately does NOT rethrow 401: the caller invokes it with `void`, so
// a rejection would surface as an unhandled promise rejection instead of
// reaching a handler.
export async function recordSuggestionOpened(
  suggestionId: number
): Promise<void> {
  const now = Date.now();
  const previousSentAt = lastSentAt.get(suggestionId);
  if (
    previousSentAt !== undefined &&
    now - previousSentAt < RESEND_COOLDOWN_MS
  ) {
    return;
  }

  // Recorded before the request rather than after it succeeds. If the API is
  // failing, leaving the attempt unrecorded would let a retry loop hammer it
  // exactly when it is least able to cope; losing one best-effort open is the
  // safer side to err on.
  lastSentAt.set(suggestionId, now);
  pruneExpired(now);

  try {
    // Declared so a quick quote ID can never be mistaken for one of these: the
    // two come from different tables and their ranges overlap.
    await axios.post(`/suggestions/${suggestionId}/opened`, null, {
      params: { pipeline: SuggestionPipelines.LoadBuilding },
    });
  } catch (error) {
    if (isExpectedTransportError(error) || isLoggedServerSide(error)) {
      return;
    }
    captureException(error, { functionName: 'recordSuggestionOpened' });
  }
}
